import { useCallback, useRef, useState } from 'react';
import useLazyQuery from 'src/hooks/useLazyQuery';
import { useLazyGetUploadSessionServerStateQuery } from 'src/newServices/uploadSessions';
import { ErrorContext } from 'src/utils/errorMappings';
import {
  compareFilesWithServer,
  type FileComparisonResult
} from 'src/utils/files/fileComparison';
import {
  decodeBinaryResponse,
  PendingFileStatus,
  type ServerStateResponse
} from 'src/utils/files/serverStateDecoder';

export interface ConflictInfo {
  count: number;
  owner: string;
}

interface PendingCheck {
  files: File[];
  resolve: (selectedFiles: File[] | null) => void;
}

export function useServerStateCheck(parentFolderId: string | number) {
  const [comparison, setComparison] = useState<FileComparisonResult | null>(
    null
  );
  const [destinationPath, setDestinationPath] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [overwrite, setOverwrite] = useState(false);
  const [conflict, setConflict] = useState<ConflictInfo | null>(null);
  const [isConflictOpen, setIsConflictOpen] = useState(false);
  const pendingRef = useRef<PendingCheck | null>(null);

  const [fetchServerState] = useLazyQuery({
    api: useLazyGetUploadSessionServerStateQuery,
    errorContext: ErrorContext.FILES
  });

  const checkServerState = useCallback(
    (files: File[], sessionId?: number): Promise<File[] | null> => {
      return new Promise((resolve) => {
        pendingRef.current = { files, resolve };
        setIsLoading(true);
        setOverwrite(false);

        fetchServerState({
          params: {
            params: {
              parent_id: parentFolderId,
              ...(sessionId ? { session_id: sessionId } : {})
            }
          },
          successCallback: async (response: any) => {
            try {
              const blob =
                response instanceof Blob
                  ? response
                  : response?.data instanceof Blob
                  ? response.data
                  : null;

              if (!blob) {
                setIsLoading(false);
                resolve(files);
                return;
              }

              const serverState =
                await decodeBinaryResponse<ServerStateResponse>(blob);

              setDestinationPath(serverState.full_path ?? '');

              const activePending = (serverState.pending_files ?? []).filter(
                (pf) =>
                  pf.st === PendingFileStatus.WAITING_FOR_FILE ||
                  pf.st === PendingFileStatus.UPLOADING
              );

              if (activePending.length > 0 && !sessionId) {
                setConflict({
                  count: activePending.length,
                  owner: (activePending[0] as any).created_by ?? ''
                });
                setIsConflictOpen(true);
                setIsLoading(false);
                return;
              }

              const hasExisting = (serverState.existing_files?.length ?? 0) > 0;
              const hasPending = (serverState.pending_files?.length ?? 0) > 0;

              if (!hasExisting && !hasPending) {
                setIsLoading(false);
                resolve(files);
                return;
              }

              const result = compareFilesWithServer(files, serverState);

              if (
                result.modifiedCount === 0 &&
                result.unchangedCount === 0 &&
                result.failedCount === 0
              ) {
                setIsLoading(false);
                resolve(files);
                return;
              }

              setComparison(result);
              setIsDialogOpen(true);
              setIsLoading(false);
            } catch (err) {
              setIsLoading(false);
              resolve(files);
            }
          },
          errorCallback: (err: unknown) => {
            setIsLoading(false);
            resolve(files);
          }
        });
      });
    },
    [parentFolderId, fetchServerState]
  );

  const handleConfirm = useCallback((selectedFiles: File[]) => {
    setIsDialogOpen(false);
    setComparison(null);
    pendingRef.current?.resolve(selectedFiles);
    pendingRef.current = null;
  }, []);

  const handleCancel = useCallback(() => {
    setIsDialogOpen(false);
    setComparison(null);
    pendingRef.current?.resolve(null);
    pendingRef.current = null;
  }, []);

  const handleConflictClose = useCallback(() => {
    setIsConflictOpen(false);
    setConflict(null);
    pendingRef.current?.resolve(null);
    pendingRef.current = null;
  }, []);

  return {
    checkServerState,
    comparison,
    destinationPath,
    isDialogOpen,
    isLoading,
    overwrite,
    setOverwrite,
    handleConfirm,
    handleCancel,
    conflict,
    isConflictOpen,
    handleConflictClose
  };
}
